import React, {
  ForwardRefRenderFunction,
  useState,
  useImperativeHandle,
  useCallback,
  memo,
  forwardRef,
} from "react";
import { Grid, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";

import useStyles from "./styles";

import Modal from "../../../../../../../../components/Generic/Modal";
import { Input, Select } from "../../../../../../../../components/Form";
import { useAppState } from "../../../../../../../../context/StateContext";
import { Status } from "../../../../../../../../util/enums";
import AutoSuggest from "../../../../../../../../components/Form/AutoSuggest";
import { Currency } from "../../../../../../../../models/currency";
import { PriceGroup } from "../../../../../../../../models/price.group";
import { Measurement } from "../../../../../../../../models/measurement";
import { Service as ServiceBooking } from "../../../../../../../../models/service";
import { AgentServiceBooking } from "../../../../../../../../models/agent.service.booking";
import { getMeasurement } from "../../../../../../../../util/getMeasurement";

export interface EditingPriceHandles {
  openModal: (agentService: AgentServiceBooking) => void;
  closeModal: () => void;
}
interface EditingPriceProps {
  onConfirmSave: (price: AgentServiceBooking) => void;
  allServices: ServiceBooking[];
  allGroups: PriceGroup[];
  allCurrencies: Currency[];
}

const EditingPrice: ForwardRefRenderFunction<EditingPriceHandles, EditingPriceProps> = (
  props,
  ref
) => {
  const { onConfirmSave, allServices, allGroups, allCurrencies } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const { currentUser } = useAppState();

  const measurement = getMeasurement();

  const [modalOpened, setModalOpened] = useState(false);
  const [actualAgentService, setActualAgentService] = useState<AgentServiceBooking>({});

  const openModal = useCallback((agentService: AgentServiceBooking) => {
    setActualAgentService(agentService);
    setModalOpened(true);
  }, []);
  const closeModal = useCallback(() => {
    setActualAgentService({});
    setModalOpened(false);
  }, []);

  useImperativeHandle(ref, () => ({
    openModal,
    closeModal,
  }));

  const onChangeInput = useCallback(
    (keys: Array<keyof AgentServiceBooking>, values: Array<any>) => {
      setActualAgentService((prev) => {
        const newObject = { ...prev };
        keys.forEach((key, keyIndex) => {
          newObject[key] = values[keyIndex];
        });
        return newObject;
      });
    },
    []
  );

  const getCurrencyById = useCallback(
    (currencyId?: string | number) =>
      allCurrencies.find((currency) => String(currency.id) === String(currencyId)),
    [allCurrencies]
  );
  const getServiceById = useCallback(
    (serviceId?: string | number) =>
      allServices.find((service) => String(service.id) === String(serviceId)),
    [allServices]
  );
  const getGroupById = useCallback(
    (groupId?: string | number) => allGroups.find((group) => String(group.id) === String(groupId)),
    [allGroups]
  );

  const getDimenstionFeeOption = useCallback((measurement?: Measurement) => {
    const fee = t("DimensionFee").replace("{dimension}", measurement?.code || "");
    const split = fee.split(" ");
    return `${split[0]?.toUpperCase()} ${split[1]}`;
  }, []);

  const onSaveData = () => {
    onConfirmSave(actualAgentService);
    closeModal();
  };

  return (
    <Modal
      open={modalOpened}
      onClose={closeModal}
      title="Edit service price"
      dataCyTitle="editingPriceTitle"
      className={classes.modalWidth}
      showModalBodyScroll={false}
      data-cy="editingPriceModal"
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        dataCySave: "submitButton",
        onCancel: closeModal,
        onSave: () => onSaveData(),
      }}
      showStatusField={false}
    >
      <section className={classes.section}>
        <Grid container direction="row" spacing={4}>
          <Grid item xs={6} md={6} lg={6}>
            <Input
              name="service"
              label={t("service")}
              value={getServiceById(actualAgentService?.serviceId)?.sellDescription || ""}
              disabled={true}
            />
          </Grid>
          <Grid item xs={6} md={6} lg={6}>
            <Input
              name="group"
              label={t("group")}
              value={getGroupById(actualAgentService?.groupId)?.name || ""}
              disabled={true}
            />
          </Grid>
        </Grid>

        <Grid container direction="row" spacing={4}>
          <Grid item xs={6} md={6} lg={6}>
            <Select
              name="status"
              label={t("status")}
              value={actualAgentService?.status || ""}
              onChange={(e) => onChangeInput(["status"], [e.target.value])}
            >
              {[Status.ACTIVE, Status.INACTIVE].map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={6} md={6} lg={6}>
            <AutoSuggest
              label={t("currency")}
              placeholder={t("searchCurrency")}
              inputValue={getCurrencyById(actualAgentService?.currencyId)?.name || ""}
              getOptionLabel={(currency?: Currency) => String(currency?.name)}
              getOptionSelected={(option?: Currency, value?: Currency) => option?.id === value?.id}
              options={allCurrencies}
              onChange={(currency: Currency) => {
                onChangeInput(["currencyId", "currency"], [currency?.id || null, currency || null]);
              }}
            />
          </Grid>
        </Grid>

        <Grid container direction="row" spacing={4}>
          <Grid item md={6} lg={6}>
            <Input
              name="m3fee"
              label={getDimenstionFeeOption(measurement)}
              leftIcon={actualAgentService?.currency?.code || "$"}
              placeholder="0.00"
              type="number"
              min={0}
              value={actualAgentService?.m3Fee || ""}
              onChange={(e) => onChangeInput(["m3Fee"], [e.target.value])}
            />
          </Grid>

          <Grid item md={6} lg={6}>
            <Input
              name="flatFee"
              label={t("flatFee")}
              leftIcon={actualAgentService?.currency?.code || "$"}
              placeholder="0.00"
              type="number"
              min={0}
              value={actualAgentService?.flatFee || ""}
              onChange={(e) => onChangeInput(["flatFee"], [e.target.value])}
            />
          </Grid>
        </Grid>

        <Grid container direction="row" spacing={4}>
          <Grid item md={6} lg={6}>
            <Input
              name="hourlyFee"
              label={t("hourlyFee")}
              leftIcon={actualAgentService?.currency?.code || "$"}
              placeholder="0.00"
              type="number"
              min={0}
              value={actualAgentService?.hourlyFee || ""}
              onChange={(e) => onChangeInput(["hourlyFee"], [e.target.value])}
            />
          </Grid>

          <Grid item md={6} lg={6}>
            <Input
              name="halfHourFee"
              label={t("halfHourFee")}
              leftIcon={actualAgentService?.currency?.code || "$"}
              placeholder="0.00"
              type="number"
              min={0}
              value={actualAgentService?.halfHourFee || ""}
              onChange={(e) => onChangeInput(["halfHourFee"], [e.target.value])}
            />
          </Grid>
        </Grid>
      </section>
    </Modal>
  );
};

export default memo(forwardRef(EditingPrice));
